import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, MagnifyingGlass, Check, Truck, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";
import Pagination from "@/components/Pagination";

const STATUS_MAP = {
  draft: { label: "Draft", color: "bg-slate-100 text-slate-600" },
  dikirim: { label: "Dikirim", color: "bg-blue-100 text-blue-700" },
  diterima: { label: "Diterima", color: "bg-emerald-100 text-emerald-700" },
  dibatalkan: { label: "Batal", color: "bg-red-100 text-red-700" },
};

export default function PurchaseOrderPage() {
  const [items, setItems] = useState([]);
  const [gudangItems, setGudangItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ gudang_item_id: "", item_nama: "", supplier: "", jumlah: 0, harga_satuan: 0, satuan: "kg", tanggal_pesan: "", keterangan: "" });

  const load = useCallback(async () => {
    const [p, g] = await Promise.all([
      api.get(`/purchase-orders?page=${page}&limit=15&search=${search}`),
      api.get("/gudang?limit=500")
    ]);
    setItems(p.data.data);
    setTotal(p.data.total);
    setGudangItems(g.data.data);
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try { await api.post("/purchase-orders", form); toast.success("PO berhasil dibuat"); setDialogOpen(false); setForm({ gudang_item_id: "", item_nama: "", supplier: "", jumlah: 0, harga_satuan: 0, satuan: "kg", tanggal_pesan: "", keterangan: "" }); load(); } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
  };

  const updateStatus = async (id, status) => {
    try { await api.put(`/purchase-orders/${id}/status`, { status }); toast.success(`Status diperbarui ke ${STATUS_MAP[status]?.label}`); load(); } catch (err) { toast.error("Gagal update status"); }
  };

  const fmt = (v) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(v || 0);

  return (
    <div data-testid="po-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-[#1A1A1A] tracking-tight" style={{ fontFamily: "Work Sans" }}>Purchase Order</h1>
          <p className="text-sm text-slate-500 mt-1">Kelola pesanan pembelian gudang</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild><Button data-testid="create-po-btn" className="bg-[#B8860B] hover:bg-[#9A7209] text-white"><Plus size={18} className="mr-2" /> Buat PO</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle style={{ fontFamily: "Work Sans" }}>Purchase Order Baru</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Item Gudang</Label>
                <Select value={form.gudang_item_id} onValueChange={(v) => { const g = gudangItems.find(x => x._id === v); setForm({ ...form, gudang_item_id: v, item_nama: g?.nama || "", satuan: g?.satuan || "kg" }); }}>
                  <SelectTrigger><SelectValue placeholder="Pilih item" /></SelectTrigger>
                  <SelectContent>{gudangItems.map(g => <SelectItem key={g._id} value={g._id}>{g.nama} (stok: {g.stok} {g.satuan})</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Supplier</Label><Input value={form.supplier} onChange={e => setForm({ ...form, supplier: e.target.value })} required /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Jumlah</Label><Input type="number" step="0.1" value={form.jumlah} onChange={e => setForm({ ...form, jumlah: parseFloat(e.target.value) || 0 })} required /></div>
                <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Harga Satuan (Rp)</Label><Input type="number" value={form.harga_satuan} onChange={e => setForm({ ...form, harga_satuan: parseFloat(e.target.value) || 0 })} required /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Tanggal Pesan</Label><Input type="date" value={form.tanggal_pesan} onChange={e => setForm({ ...form, tanggal_pesan: e.target.value })} required /></div>
                <div className="p-3 bg-slate-50 rounded-lg text-center"><p className="text-[10px] uppercase text-slate-500 font-semibold">Total</p><p className="text-lg font-bold text-[#1A1A1A]" style={{ fontFamily: "Work Sans" }}>{fmt(form.jumlah * form.harga_satuan)}</p></div>
              </div>
              <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Keterangan</Label><Input value={form.keterangan} onChange={e => setForm({ ...form, keterangan: e.target.value })} /></div>
              <Button type="submit" className="w-full bg-[#B8860B] hover:bg-[#9A7209] text-white">Simpan PO</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative w-full sm:w-72"><MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><Input placeholder="Cari PO..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} className="pl-9 h-9" /></div>

      <Card className="bg-white border border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Tanggal</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Item</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Supplier</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-center">Jumlah</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-right">Total Harga</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Aksi</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">Belum ada PO</TableCell></TableRow>
              ) : items.map(po => (
                <TableRow key={po._id} className="hover:bg-slate-50">
                  <TableCell className="text-xs">{po.tanggal_pesan}</TableCell>
                  <TableCell className="text-sm font-medium">{po.item_nama}</TableCell>
                  <TableCell className="text-sm">{po.supplier}</TableCell>
                  <TableCell className="text-sm text-center font-semibold">{po.jumlah} {po.satuan}</TableCell>
                  <TableCell className="text-sm text-right font-medium text-[#B8860B]">{fmt(po.total_harga)}</TableCell>
                  <TableCell><Badge className={`${STATUS_MAP[po.status]?.color || ""} hover:${STATUS_MAP[po.status]?.color || ""} text-[10px]`}>{STATUS_MAP[po.status]?.label || po.status}</Badge></TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {po.status === "draft" && <Button size="sm" variant="ghost" onClick={() => updateStatus(po._id, "dikirim")} className="text-blue-600 h-7 text-xs"><Truck size={14} className="mr-1" />Kirim</Button>}
                      {po.status === "dikirim" && <Button size="sm" variant="ghost" onClick={() => updateStatus(po._id, "diterima")} className="text-emerald-600 h-7 text-xs"><Check size={14} className="mr-1" />Terima</Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="px-4 pb-3"><Pagination page={page} limit={15} total={total} onPageChange={setPage} /></div>
        </CardContent>
      </Card>
    </div>
  );
}
